import { addDays, isWeekend, isWithinInterval, parseISO, startOfDay } from 'date-fns';

import { TimeOffRequest, TimeOffRequestDay } from '@/modules/time-off/types/time-off-request';

import { sortTimeOffRequests } from '../../../helpers/time-off-sort.helper';
import { TimeOffDetailsCard } from '../../time-off/time-off-details-card';
import { WeekExpandableRow } from '../week-expandable-row';

interface WeekTimeOff {
  request: TimeOffRequest;
  dayLines: TimeOffRequestDay[];
}

/**
 * Build the week's time-off list: each request that has at least one weekday in
 * the visible week, with its days clipped to that week (weekends excluded —
 * members can't take time off on weekends).
 */
const buildWeekTimeOffs = (requests: TimeOffRequest[], weekStart: Date): WeekTimeOff[] => {
  const weekInterval = { start: startOfDay(weekStart), end: startOfDay(addDays(weekStart, 6)) };

  return sortTimeOffRequests(requests)
    .map(request => ({
      request,
      dayLines: request.days.filter(day => {
        const date = parseISO(day.date);
        return !isWeekend(date) && isWithinInterval(startOfDay(date), weekInterval);
      }),
    }))
    .filter(({ dayLines }) => dayLines.length > 0);
};

interface TimeOffExpandedRowProps {
  requests: TimeOffRequest[];
  weekStart: Date;
  isExpanded: boolean;
}

/**
 * Expanded panel under the week time-off strip listing each time-off request for the
 * week with its general info and the week's days it covers (reusing the shared
 * `TimeOffDetailsCard`). All data is already loaded, so unlike the project expanded
 * row this needs no extra fetch. The height transition is handled by the shared
 * `WeekExpandableRow`.
 */
export function TimeOffExpandedRow({ requests, weekStart, isExpanded }: TimeOffExpandedRowProps) {
  return (
    <WeekExpandableRow isExpanded={isExpanded}>
      {hasOpened =>
        hasOpened && (
          <div className="flex flex-col gap-2 border-b bg-muted/20 p-4">
            {buildWeekTimeOffs(requests, weekStart).map(({ request, dayLines }) => (
              <TimeOffDetailsCard key={request.id} request={request} dayLines={dayLines} />
            ))}
          </div>
        )
      }
    </WeekExpandableRow>
  );
}
